import React, { FC } from 'react';
import DemoBadge from 'app/components/DemoBadge';
import { BrandLogo, Icon } from '@kontentino/ui';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { PostDetail } from 'types/PostDetail';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { isLocalPostLocked } from 'app/modules/posts/utils/globalPosts';
import { faLockKeyhole } from '@fortawesome/pro-solid-svg-icons/faLockKeyhole';
import UserPermissionGate from 'components/UserPermissionGate';
import { useUpdatePostStatus } from 'app/modules/posts/modules/post/hooks/useUpdatePostStatus';
import WorkflowStatusBadgeDropdown from 'app/components/workflowStatus/WorkflowStatusBadgeDropdown';
import { UserPermission } from 'constants/userPermission';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';
import { useHistoryQueryParams } from 'app/hooks/useHistoryQueryParams';
import PostsConfig from 'app/config/posts';
import { getApprovalCommunicationTabForStatus } from 'app/modules/posts/utils/approvalCommunicationTab';

type Props = {
  post: PostDetail & Partial<Pick<PostDetail, 'assigned_users'>>;
};

const BriefInfo: FC<Props> = ({ post }) => {
  const updatePostStatus = useUpdatePostStatus();
  const queryParamHistory = useHistoryQueryParams();

  const handleSelectStatus = (status: number) => {
    updatePostStatus(post.id, status, {
      wasPublished: post.was_published,
      pageName: post.page?.name,
    });

    const approvalTab = getApprovalCommunicationTabForStatus(status);
    if (approvalTab) {
      queryParamHistory.setQueryParameter(
        PostsConfig.COMMUNICATION_TAB_QUERY_PARAM_KEY,
        approvalTab,
      );
    }
  };

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold">
      {post.globalPost && (
        <>
          <Icon icon={faGlobe} className="tw-text-grayscale-100" size="lg" />
          {isLocalPostLocked(post) && (
            <Icon
              icon={faLockKeyhole}
              className="tw-text-warning-100"
              size="lg"
            />
          )}

          <Icon
            icon={faChevronRight}
            className="tw-text-grayscale-100"
            size="lg"
          />
        </>
      )}

      {post.isGlobal && (
        <>
          <Icon icon={faGlobe} className="tw-text-grayscale-100" size="lg" />
          <Icon
            icon={faChevronRight}
            className="tw-text-grayscale-100"
            size="lg"
          />
        </>
      )}

      <BrandLogo
        src={post.page.logo?.src}
        name={post.page.name}
        size={28}
        socialMediaType={getSocialMediaTypeForBrandLogo(post.page.type)}
        iconSize={getYtLogoGuidelineSize(12, post.page.type)}
        className="tw-hidden md:tw-block"
      />

      <div
        className="tw-relative tw-hidden tw-max-w-xl tw-truncate md:tw-block"
        translate="no"
        data-cy="post-header-profile-name"
      >
        {post.page.name}
      </div>
      {post.page.isDemo && <DemoBadge />}

      {!isLocalPostLocked(post) && (
        <UserPermissionGate
          scopes={UserPermission.POST_EDIT_STATUS}
          deniedProps={{
            isDisabled: true,
          }}
        >
          <WorkflowStatusBadgeDropdown
            post={post}
            onSelectStatus={handleSelectStatus}
            isLoading={updatePostStatus.isLoading}
            loadingStatus={updatePostStatus.pendingStatus}
          />
        </UserPermissionGate>
      )}
    </div>
  );
};

export default BriefInfo;
